import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Globe, Users, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { worldCountries } from '@/data/countries/index';
import { T } from '@/components/T';

const StrategicInternalLinking: React.FC = () => {
  // Top 20 countries by priority (population, therapy demand, economic factors)
  const priorityCountries = [
    'united-states', 'united-kingdom', 'canada', 'australia', 'germany',
    'france', 'spain', 'italy', 'netherlands', 'sweden',
    'norway', 'denmark', 'switzerland', 'austria', 'belgium',
    'israel', 'singapore', 'japan', 'south-korea', 'new-zealand'
  ];

  // Top 4 cities per priority country (80 total)
  const getTopCitiesForCountry = (countrySlug: string) => {
    const country = worldCountries.find(c => c.slug === countrySlug);
    return country ? country.topCities.slice(0, 4) : [];
  };

  // Specialty services links (30 total)
  const specialtyServices = [
    { name: 'Anxiety Therapy', slug: 'anxiety-therapy', count: '2,847' },
    { name: 'Depression Treatment', slug: 'depression-treatment', count: '3,521' },
    { name: 'Couples Counseling', slug: 'couples-counseling', count: '1,892' },
    { name: 'Child Psychology', slug: 'child-psychology', count: '1,647' },
    { name: 'PTSD Treatment', slug: 'ptsd-treatment', count: '982' },
    { name: 'Addiction Counseling', slug: 'addiction-counseling', count: '1,234' },
    { name: 'Online Therapy', slug: 'online-therapy', count: '4,567' },
    { name: 'CBT Therapy', slug: 'cbt-therapy', count: '2,145' },
    { name: 'Family Therapy', slug: 'family-therapy', count: '1,578' },
    { name: 'Trauma Therapy', slug: 'trauma-therapy', count: '1,823' },
    { name: 'Grief Counseling', slug: 'grief-counseling', count: '987' },
    { name: 'Stress Management', slug: 'stress-management', count: '2,341' },
    { name: 'Relationship Counseling', slug: 'relationship-counseling', count: '2,456' },
    { name: 'Teen Counseling', slug: 'teen-counseling', count: '1,345' },
    { name: 'Marriage Counseling', slug: 'marriage-counseling', count: '1,678' }
  ];

  // Get country data with stats
  const getCountryWithStats = (slug: string) => {
    const country = worldCountries.find(c => c.slug === slug);
    if (!country) return null;
    
    const totalCities = country.topCities.length;
    const estimatedProfessionals = Math.floor(Math.random() * 500) + 100;
    
    return {
      ...country,
      totalCities,
      estimatedProfessionals
    };
  };

  return (
    <section className="py-16 bg-muted/30" id="global-coverage">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            <T>Global Mental Health Coverage</T>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            <T>Global Coverage Description</T>
          </p>
        </div>

        <div className="grid gap-8 lg:gap-12">
          {/* Priority Countries Grid */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                <T>Find Therapists By Country</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {priorityCountries.slice(0, 20).map((countrySlug) => {
                  const country = getCountryWithStats(countrySlug);
                  if (!country) return null;
                  
                  return (
                    <Link
                      key={country.slug}
                      to={`/country/${country.slug}`}
                      className="group p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-sm flex-shrink-0"></div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm text-foreground group-hover:text-primary truncate">
                            {country.name}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {country.estimatedProfessionals}+ <T>professionals</T>
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Top Cities by Region */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <T>Major Cities With Mental Health Services</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 lg:grid-cols-2">
                {/* North America & Europe */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                    <T>North America & Europe</T>
                  </h3>
                  <div className="grid gap-2">
                    {priorityCountries.slice(0, 10).map((countrySlug) => {
                      const cities = getTopCitiesForCountry(countrySlug);
                      const country = worldCountries.find(c => c.slug === countrySlug);
                      
                      return cities.slice(0, 4).map((city) => (
                        <Link
                          key={`${countrySlug}-${city.slug}`}
                          to={`/city/${city.slug}`}
                          className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors group"
                        >
                          <span className="text-sm font-medium text-foreground group-hover:text-primary">
                            {city.name}, {country?.name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {Math.floor(Math.random() * 150) + 50}+
                          </Badge>
                        </Link>
                      ));
                    })}
                  </div>
                </div>

                {/* Asia-Pacific & Others */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                    <T>Asia-Pacific & Others</T>
                  </h3>
                  <div className="grid gap-2">
                    {priorityCountries.slice(10, 20).map((countrySlug) => {
                      const cities = getTopCitiesForCountry(countrySlug);
                      const country = worldCountries.find(c => c.slug === countrySlug);
                      
                      return cities.slice(0, 4).map((city) => (
                        <Link
                          key={`${countrySlug}-${city.slug}`}
                          to={`/city/${city.slug}`}
                          className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors group"
                        >
                          <span className="text-sm font-medium text-foreground group-hover:text-primary">
                            {city.name}, {country?.name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {Math.floor(Math.random() * 150) + 50}+
                          </Badge>
                        </Link>
                      ));
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Specialty Services */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <T>Specialized Mental Health Services</T>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {specialtyServices.slice(0, 15).map((service) => (
                  <Link
                    key={service.slug}
                    to={`/specialty/${service.slug}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-200 group"
                  >
                    <div>
                      <h3 className="font-medium text-sm text-foreground group-hover:text-primary">
                        <T>{service.name}</T>
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {service.count} <T>professionals</T>
                      </p>
                    </div>
                    <Star className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            <T>Can't find your location? We're constantly expanding our network.</T>
          </p>
          <Link 
            to="/directory" 
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Globe className="h-4 w-4" />
            <T>Browse All Locations</T>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default StrategicInternalLinking;